export const ACCOUNTS_KEY = "apps.accounts";
export const ACTIVE_ACCOUNT_KEY = "apps.activeAccount";
export const IGNORED_MAILBOX_IDENTITIES_KEY = "apps.ignoredMailboxIdentities";
/** Bare local-part for SpamAssassin / Dovecot-style matching. */
export function accountUsername(email) {
    const normalized = email.trim().toLowerCase();
    const at = normalized.indexOf("@");
    return at === -1 ? normalized : normalized.slice(0, at);
}
/**
 * Storage map key: prefer the full email when present so Kerio accounts
 * that login as user@domain keep their identity (and don't collide across domains).
 */
export function accountStorageKey(email) {
    return email.trim().toLowerCase();
}
export function normalizeMailboxAlias(alias) {
    const normalized = alias.trim().toLowerCase();
    if (!normalized.includes("@")) {
        throw new Error("Mailbox address must be a full email address");
    }
    return normalized;
}
/** Normalize a mailbox identity for ignore matching (email or bare label). */
export function normalizeIgnoredMailboxIdentity(identity) {
    const normalized = identity.trim().toLowerCase();
    if (!normalized) {
        throw new Error("Mailbox identity is required");
    }
    return normalized;
}
async function loadIgnoredMailboxIdentities(backend) {
    const result = await backend.get([IGNORED_MAILBOX_IDENTITIES_KEY]);
    const raw = result[IGNORED_MAILBOX_IDENTITIES_KEY];
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw
        .filter((value) => typeof value === "string")
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
}
/**
 * Load accounts and migrate legacy bare-username keys to full email when
 * credentials.imapAccount still carries (or can be restored as) a full address.
 */
async function loadAccountsMap(backend) {
    const result = await backend.get([ACCOUNTS_KEY, ACTIVE_ACCOUNT_KEY]);
    const raw = result[ACCOUNTS_KEY] ?? {};
    const accounts = {};
    let migrated = false;
    for (const [key, credentials] of Object.entries(raw)) {
        if (!credentials || typeof credentials !== "object") {
            continue;
        }
        const imapAccount = (credentials.imapAccount || key).trim().toLowerCase();
        const storageKey = accountStorageKey(imapAccount);
        const next = {
            ...credentials,
            imapAccount,
        };
        // If an older bare key and a full-email key would collide, prefer full email.
        if (accounts[storageKey] && storageKey !== key) {
            // Keep the entry that already has a domain when possible.
            const existing = accounts[storageKey];
            if (!existing.imapAccount.includes("@") &&
                next.imapAccount.includes("@")) {
                accounts[storageKey] = next;
                migrated = true;
            }
            continue;
        }
        if (storageKey !== key) {
            migrated = true;
        }
        accounts[storageKey] = next;
    }
    if (migrated) {
        const activeRaw = result[ACTIVE_ACCOUNT_KEY];
        let active = typeof activeRaw === "string" ? accountStorageKey(activeRaw) : null;
        if (active && !accounts[active]) {
            // Legacy active was bare username — resolve to a full-email key.
            const bare = accountUsername(active);
            const match = Object.keys(accounts).find((k) => accountUsername(k) === bare || accountUsername(accounts[k].imapAccount) === bare);
            active = match ?? Object.keys(accounts).sort((a, b) => a.localeCompare(b))[0] ?? null;
        }
        await backend.set({
            [ACCOUNTS_KEY]: accounts,
            ...(active ? { [ACTIVE_ACCOUNT_KEY]: active } : {}),
        });
    }
    return accounts;
}
function resolveAccountKey(accounts, account) {
    const normalized = accountStorageKey(account);
    if (accounts[normalized]) {
        return normalized;
    }
    const bare = accountUsername(normalized);
    for (const [key, credentials] of Object.entries(accounts)) {
        if (accountUsername(key) === bare ||
            accountUsername(credentials.imapAccount) === bare ||
            credentials.imapAccount.toLowerCase() === normalized) {
            return key;
        }
    }
    return null;
}
export function createMultiAccountStorage(backend) {
    return {
        async listAccounts() {
            const accounts = await loadAccountsMap(backend);
            return Object.values(accounts)
                .map((credentials) => credentials.imapAccount)
                .sort((a, b) => a.localeCompare(b));
        },
        async getActiveAccount() {
            const accounts = await loadAccountsMap(backend);
            const keys = Object.keys(accounts);
            if (keys.length === 0) {
                return null;
            }
            const result = await backend.get([ACTIVE_ACCOUNT_KEY]);
            const active = result[ACTIVE_ACCOUNT_KEY];
            if (typeof active === "string") {
                const key = resolveAccountKey(accounts, active);
                if (key) {
                    return accounts[key].imapAccount;
                }
            }
            return accounts[keys.sort((a, b) => a.localeCompare(b))[0]].imapAccount;
        },
        async setActiveAccount(account) {
            const accounts = await loadAccountsMap(backend);
            const key = resolveAccountKey(accounts, account);
            if (!key) {
                throw new Error(`Account is not paired: ${account}`);
            }
            await backend.set({ [ACTIVE_ACCOUNT_KEY]: key });
        },
        async getCredentials(account) {
            const accounts = await loadAccountsMap(backend);
            if (Object.keys(accounts).length === 0) {
                return null;
            }
            const target = account !== undefined
                ? resolveAccountKey(accounts, account)
                : resolveAccountKey(accounts, (await this.getActiveAccount()) ?? "");
            if (!target) {
                return null;
            }
            return accounts[target] ?? null;
        },
        async setCredentials(credentials) {
            const accounts = await loadAccountsMap(backend);
            const imapAccount = credentials.imapAccount.trim().toLowerCase();
            const key = accountStorageKey(imapAccount);
            // Drop any legacy bare-username duplicate for the same local-part.
            const bare = accountUsername(imapAccount);
            for (const existingKey of Object.keys(accounts)) {
                if (existingKey !== key &&
                    !existingKey.includes("@") &&
                    accountUsername(existingKey) === bare) {
                    delete accounts[existingKey];
                }
            }
            accounts[key] = { ...credentials, imapAccount };
            await backend.set({
                [ACCOUNTS_KEY]: accounts,
                [ACTIVE_ACCOUNT_KEY]: key,
            });
        },
        async removeAccount(account) {
            const accounts = await loadAccountsMap(backend);
            const key = resolveAccountKey(accounts, account);
            if (!key) {
                return;
            }
            delete accounts[key];
            const remaining = Object.keys(accounts);
            const result = await backend.get([ACTIVE_ACCOUNT_KEY]);
            const activeRaw = typeof result[ACTIVE_ACCOUNT_KEY] === "string"
                ? result[ACTIVE_ACCOUNT_KEY]
                : null;
            const activeKey = activeRaw ? resolveAccountKey(accounts, activeRaw) : null;
            if (remaining.length === 0) {
                await backend.remove([ACCOUNTS_KEY, ACTIVE_ACCOUNT_KEY]);
                return;
            }
            const nextActive = activeKey && accounts[activeKey]
                ? activeKey
                : remaining.sort((a, b) => a.localeCompare(b))[0];
            await backend.set({
                [ACCOUNTS_KEY]: accounts,
                [ACTIVE_ACCOUNT_KEY]: nextActive,
            });
        },
        async clearAllCredentials() {
            await backend.remove([ACCOUNTS_KEY, ACTIVE_ACCOUNT_KEY]);
        },
        async listMailboxAliases(account) {
            const credentials = await this.getCredentials(account);
            if (!credentials) {
                return [];
            }
            const aliases = credentials.mailboxAliases ?? [];
            return [...aliases].sort((a, b) => a.localeCompare(b));
        },
        async addMailboxAlias(account, alias) {
            const accounts = await loadAccountsMap(backend);
            const key = resolveAccountKey(accounts, account);
            if (!key) {
                throw new Error(`Account is not paired: ${account}`);
            }
            const normalizedAlias = normalizeMailboxAlias(alias);
            const credentials = accounts[key];
            if (normalizedAlias === credentials.imapAccount.toLowerCase()) {
                throw new Error("This address already matches the paired account");
            }
            const aliases = credentials.mailboxAliases ?? [];
            if (aliases.includes(normalizedAlias)) {
                throw new Error("This mailbox address is already added");
            }
            credentials.mailboxAliases = [...aliases, normalizedAlias].sort((a, b) => a.localeCompare(b));
            accounts[key] = credentials;
            await backend.set({ [ACCOUNTS_KEY]: accounts });
        },
        async removeMailboxAlias(account, alias) {
            const accounts = await loadAccountsMap(backend);
            const key = resolveAccountKey(accounts, account);
            if (!key) {
                return;
            }
            const normalizedAlias = normalizeMailboxAlias(alias);
            const credentials = accounts[key];
            const aliases = credentials.mailboxAliases ?? [];
            const nextAliases = aliases.filter((existing) => existing !== normalizedAlias);
            if (nextAliases.length === aliases.length) {
                return;
            }
            credentials.mailboxAliases = nextAliases;
            accounts[key] = credentials;
            await backend.set({ [ACCOUNTS_KEY]: accounts });
        },
        async listIgnoredMailboxIdentities() {
            return loadIgnoredMailboxIdentities(backend);
        },
        async addIgnoredMailboxIdentity(identity) {
            const normalized = normalizeIgnoredMailboxIdentity(identity);
            const existing = await loadIgnoredMailboxIdentities(backend);
            if (existing.includes(normalized)) {
                return;
            }
            const next = [...existing, normalized].sort((a, b) => a.localeCompare(b));
            await backend.set({ [IGNORED_MAILBOX_IDENTITIES_KEY]: next });
        },
        async removeIgnoredMailboxIdentity(identity) {
            const normalized = normalizeIgnoredMailboxIdentity(identity);
            const existing = await loadIgnoredMailboxIdentities(backend);
            const next = existing.filter((value) => value !== normalized);
            if (next.length === existing.length) {
                return;
            }
            if (next.length === 0) {
                await backend.remove([IGNORED_MAILBOX_IDENTITIES_KEY]);
                return;
            }
            await backend.set({ [IGNORED_MAILBOX_IDENTITIES_KEY]: next });
        },
    };
}
